import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { CommonRouteQueryComposable } from '@/composables/routeQueries/useCommonRouteQuery';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isCancel } from 'axios';

import { useLoaderStore } from '@/stores/useLoaderStore';
import { useUserStore } from '@/stores/useUserStore';
import * as dateHelpers from '@/helpers/date';
import { fetchGetAirroadJobs2 } from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';

export interface BookedAirRoadViewDataComposable {
  isFetchingAirRoadBookedJobsData: Readonly<Ref<boolean>>;

  airRoadBookedJobsData: Readonly<
    Ref<ReadonlyDeep<openapi.AirroadList2ContentOpenapi> | undefined>
  >;

  airRoadBookedJobsDataError: Readonly<Ref<unknown | undefined>>;

  refreshAirRoadBookedJobsData: () => Promise<void>;

  reload: () => BookedAirRoadViewDataComposable;
}

export default function useBookedAirRoadViewData(
  routeQuery: CommonRouteQueryComposable,
): BookedAirRoadViewDataComposable {
  const route = useRoute();
  const userStore = useUserStore();
  const {
    queryCurPage,
    queryPageSize,
    queryDates,
    queryStatus,
    queryCustomer,
    querySearch,
    queryDateType,
  } = routeQuery;

  const userTimeZone = computed(() => userStore.user.timezone);

  const loaderStore = useLoaderStore();

  let airRoadBookedJobsDataController: AbortController | undefined;

  const airRoadBookedJobsDataFetchCounter = ref(0);

  const isFetchingAirRoadBookedJobsData = computed(
    () => !!airRoadBookedJobsDataFetchCounter.value,
  );

  const airRoadBookedJobsData =
    ref<ReadonlyDeep<openapi.AirroadList2ContentOpenapi>>();

  const airRoadBookedJobsDataError = ref();

  function clear(): void {
    if (airRoadBookedJobsDataController) {
      airRoadBookedJobsDataController.abort();
    }
  }

  onUnmounted(() => {
    clear();
  });

  async function _fetchAirRoadBookedJobsData(): Promise<
    ReadonlyDeep<openapi.AirroadList2ContentOpenapi> | undefined
  > {
    if (airRoadBookedJobsDataController) {
      airRoadBookedJobsDataController.abort();
    }
    if (!queryDates.value) return;
    const apiZonedFormattedDates = [
      dateHelpers.getApiZonedFormattedDate(
        queryDates.value[0],
        userTimeZone.value,
      ),
      dateHelpers.getApiZonedFormattedDate(
        queryDates.value[1],
        userTimeZone.value,
      ),
    ];
    const endpointParams: openapi.AirroadList2QueryOpenapi = {
      page: queryCurPage.value,
      page_size: queryPageSize.value,
      ordering: '-rockhopper_date,eta',
      search: querySearch.value,
      status: Number.isNaN(queryStatus.value) ? undefined : queryStatus.value,
      customer_code: queryCustomer.value,
    };
    if (queryDateType.value === 'rh_job_date') {
      endpointParams.rockhopper_date_gte = apiZonedFormattedDates[0];
      endpointParams.rockhopper_date_lte = apiZonedFormattedDates[1];
    } else if (queryDateType.value === 'ar_created') {
      endpointParams.created_at_gte = apiZonedFormattedDates[0];
      endpointParams.created_at_lte = apiZonedFormattedDates[1];
    } else if (queryDateType.value === 'required') {
      endpointParams.required_at_gte = apiZonedFormattedDates[0];
      endpointParams.required_at_lte = apiZonedFormattedDates[1];
    } else {
      return;
    }
    airRoadBookedJobsDataController = new AbortController();
    return await fetchGetAirroadJobs2(endpointParams, {
      signal: airRoadBookedJobsDataController.signal,
    })
      .then(function (data) {
        airRoadBookedJobsDataError.value = undefined;
        return data;
      })
      .catch(function (err: unknown) {
        if (isCancel(err)) return undefined;
        airRoadBookedJobsDataError.value = err;
        throw err;
      });
  }

  async function refreshAirRoadBookedJobsData(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    airRoadBookedJobsDataFetchCounter.value++;
    try {
      airRoadBookedJobsData.value = await _fetchAirRoadBookedJobsData();
    } finally {
      airRoadBookedJobsDataFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  function reload(): BookedAirRoadViewDataComposable {
    void refreshAirRoadBookedJobsData();
    return {
      isFetchingAirRoadBookedJobsData,
      airRoadBookedJobsData,
      airRoadBookedJobsDataError,
      refreshAirRoadBookedJobsData,
      reload,
    };
  }

  watch(
    [
      queryCurPage,
      queryPageSize,
      queryDates,
      queryStatus,
      queryCustomer,
      querySearch,
      queryDateType,
    ],
    async function (newValues, oldValues) {
      if (route.name !== routeNames.AIR_ROAD_BOOKED) return;
      reload();
    },
    { immediate: true },
  );

  return {
    isFetchingAirRoadBookedJobsData,
    airRoadBookedJobsData,
    airRoadBookedJobsDataError,
    refreshAirRoadBookedJobsData,
    reload,
  };
}
